import type {
  BulkSetInspectionScheduleActiveRequest,
  GetInspectionScheduleListRequestV2,
  GetListResponseOfInspectionScheduleResponse,
  InspectionScheduleRequest,
  InspectionScheduleResponse,
} from 'src/api/ProactApiClient';
import {inspectionScheduleApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Newtonsoft sends explicit `null` where the generated client types `| undefined`;
// normalize once here so consumers can rely on `undefined` semantics.
const normalizeSchedule = (
  schedule: InspectionScheduleResponse
): InspectionScheduleResponse => ({
  ...schedule,
  siteTimeZone: schedule.siteTimeZone ?? undefined,
  intervalDays: schedule.intervalDays ?? undefined,
  clientId: schedule.clientId ?? undefined,
  clientName: schedule.clientName ?? undefined,
  latestInspectionState: schedule.latestInspectionState ?? undefined,
  latestInspectionDueAt: schedule.latestInspectionDueAt ?? undefined,
  healthIssues: schedule.healthIssues ?? [],
});

export const inspectionScheduleApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getInspectionScheduleListV2: builder.query<
      GetListResponseOfInspectionScheduleResponse,
      GetInspectionScheduleListRequestV2
    >({
      queryFn: clientQuery(async request => {
        const response = await inspectionScheduleApiClient.listV2(request);
        return {...response, data: (response.data ?? []).map(normalizeSchedule)};
      }),
      providesTags: ['InspectionSchedules'],
    }),
    getInspectionSchedule: builder.query<InspectionScheduleResponse, number>({
      queryFn: clientQuery(async id =>
        normalizeSchedule(await inspectionScheduleApiClient.get(id))
      ),
      providesTags: ['InspectionSchedules'],
    }),
    getInspectionSchedulesBySite: builder.query<
      InspectionScheduleResponse[],
      number
    >({
      queryFn: clientQuery(async siteId => {
        const schedules = await inspectionScheduleApiClient.listBySite(siteId);
        return schedules.map(normalizeSchedule);
      }),
      providesTags: ['InspectionSchedules'],
    }),
    createInspectionSchedule: builder.mutation<
      InspectionScheduleResponse,
      InspectionScheduleRequest
    >({
      queryFn: clientQuery(async request =>
        normalizeSchedule(await inspectionScheduleApiClient.create(request))
      ),
      invalidatesTags: ['InspectionSchedules'],
    }),
    updateInspectionSchedule: builder.mutation<
      InspectionScheduleResponse,
      InspectionScheduleRequest
    >({
      queryFn: clientQuery(async request =>
        normalizeSchedule(await inspectionScheduleApiClient.update(request))
      ),
      invalidatesTags: ['InspectionSchedules'],
    }),
    deleteInspectionSchedule: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => inspectionScheduleApiClient.delete(id)),
      invalidatesTags: ['InspectionSchedules'],
    }),
    bulkSetInspectionScheduleActive: builder.mutation<
      number,
      BulkSetInspectionScheduleActiveRequest
    >({
      queryFn: clientQuery(request =>
        inspectionScheduleApiClient.bulkSetActive(request)
      ),
      invalidatesTags: ['InspectionSchedules'],
    }),
  }),
});

export const {
  useGetInspectionScheduleListV2Query,
  useGetInspectionScheduleQuery,
  useGetInspectionSchedulesBySiteQuery,
  useCreateInspectionScheduleMutation,
  useUpdateInspectionScheduleMutation,
  useDeleteInspectionScheduleMutation,
  useBulkSetInspectionScheduleActiveMutation,
} = inspectionScheduleApi;
